/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import { action } from '@ember/object';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import type { WithBoundArgs } from '@glint/template';

import type { IBtnColor, TButtonSize } from '../bv-btn';
import type BvBtnComponent from '../bv-btn';
import type { TTooltipSide } from '../bv-tooltip';

export interface IBvBtnGroupContent {
  iconLeft?: string;
  iconRight?: string;
  tooltipText?: string;
  disabled?: boolean;
  action?: () => void;
  hidden?: boolean;
  key?: string;
  label?: string;
}

interface BvBtnGroupSignature {
  Args: {
    color?: IBtnColor;
    size?: TButtonSize;
    activeValue?: string;
    content?: IBvBtnGroupContent[];
    labelPath?: string;
    keyPath?: string;
    nullable?: boolean;
    outline?: boolean;
    disabled?: boolean;
    subtle?: boolean;
    plain?: boolean;
    rounded?: boolean;
    fullWidth?: boolean;
    isLoading?: boolean;
    justified?: boolean;
    segmented?: boolean;
    tooltipSide?: TTooltipSide;
    onChange?: (value?: string | null) => void;
  };
  Blocks: {
    default: [
      {
        btn: WithBoundArgs<
          typeof BvBtnComponent,
          | 'outline'
          | 'color'
          | 'size'
          | 'disabled'
          | 'rounded'
          | 'subtle'
          | 'plain'
          | 'fullWidth'
          | 'tooltipSide'
          | 'isLoading'
        >;
      }
    ];
  };
  Element: HTMLDivElement;
}

export default class BvBtnGroupComponent extends Component<BvBtnGroupSignature> {
  // eslint-disable-next-line ember/no-tracked-properties-from-args
  @tracked activeValue?: string | null = this.args.activeValue;

  segmented = true;

  get content() {
    return this.args.content ?? [];
  }

  get labelPath() {
    return this.args.labelPath ?? 'label';
  }

  get keyPath() {
    return this.args.keyPath ?? 'key';
  }

  @action
  handleDidUpdate(_el: HTMLElement, [key]: [string | undefined]) {
    this.setActiveValue(key);
  }

  @action
  handleChange(key: string) {
    this.setActiveValue(key);
    this.args.onChange?.(this.activeValue);
  }

  @action
  handleClick(evt: InputEvent) {
    evt.stopPropagation();
  }

  setActiveValue(value?: string) {
    if (this.activeValue !== value) {
      const { nullable, activeValue } = this.args;
      const shouldBeNull = nullable && activeValue === value;
      this.activeValue = shouldBeNull ? null : value;
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    BvBtnGroup: typeof BvBtnGroupComponent;
    'bv-btn-group': typeof BvBtnGroupComponent;
  }
}
